// @ts-strict-ignore
import * as React from 'react';
import {Location, NavigateFunction} from 'react-router';
import {PureComponent} from 'react';
import kebabCase from 'lodash/kebabCase';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {QueryContext, QueryContextManager} from 'common/query-context';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {reporter} from 'common/report/error-reporting';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {parseJsUrl, serializeJsUrl} from 'common/report/util/url';
import {ReportTracker} from 'common/report/report-tracker';
import {FlowsReport} from 'common/report/flows/models/report';
import {convertAnchorFlowsToFlowsReport} from 'common/report/flows/models/converter';
import {ChartTypeIcon, FlowAnchorPosition, FlowsBookmark} from 'common/report/flows/models/types';
import {DownloadMode, ProjectFeatureFlag, ReportName, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from 'common/report/constants';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {
  getDownloadModeFromLocation,
  getProjectId,
  getReportName,
  getReportUrl,
  getWorkspaceId,
  moveReport,
} from 'common/report/util';
import {removeRecentItemById, searchUtils, transformBookmarkIntoRecentItem} from 'common/report/util/search/index';
import {TrackedEvents as ConversionTrackedEvents} from 'common/query-builder/conversion-query-options/tracking';
import {BookmarkOverrides, DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {getBookmarkParamsWithOverrides} from 'common/report/flows/util/bookmark-overrides';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {BookmarkState, cancelWorkflow, captureWorkflowTiming, Workflow} from 'common/util/workflow-timing';
import {CountType} from 'common/report/funnels/models/types';
import {ConversionWindowUnit} from 'common/types/reports/bookmark';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {BookmarkStore} from 'common/report/stores/bookmarks';
import {ProductionTrackingEvents} from 'common/report/flows/tracking';
import {parseUri, replaceQueryParamToFragment} from 'common/util';
import {Bookmark, DisplayBookmark} from 'common/report/dashboards/types';
import {DeleteEvent, ReportEditorReact, SaveEvent, UpdateEvent} from 'common/report-editor';
import {getReportTracker} from 'common/report/report-manager/tracking';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {isValidBookmarkURLSlug} from 'common/report/util/bookmark-urls';
import {BackboneStyleRoutes} from 'common/contexts/react/react-router';
import {ReportEditorActions} from 'common/report/contexts/report-editor-actions';
import {getCardInDashboardHref, getRouteToCurrentDashboard} from 'common/report/util/boards';
import {ApiResponseError} from 'common/util/shared-client';
import {getLocation, navigate} from 'common/util/url';
import {ReportLayoutManager} from 'common/report/contexts/report-layout';
import {NavManager} from 'common/contexts/nav-manager/types';
import {ContentSidenavContextValue} from 'common/widgets/content-sidenav/types';

import {flowsQueryStartedWorkflowComplete} from '../util/workflow';

type State = {
  bookmark: Bookmark<FlowsBookmark>;
  bookmarkURLSlug?: string;
  lastReportQueryContext: QueryContext;
  loading: boolean;
  isSavingBookmark: boolean;
  longUrlCount: number;
  originDashboard: DashboardOrigin;
  report: FlowsReport;
};

type Props = {
  queryContextManager: QueryContextManager;
  bookmarkStore: BookmarkStore;
  currentProject: CurrentProjectResource;
  reportLayoutManager: ReportLayoutManager;
  navManager: NavManager;
  navigate: NavigateFunction;
  location: Location;
  reportEditorActions: ReportEditorActions;
  contentSidenavContext: ContentSidenavContextValue | undefined;
};

export class FlowsApp extends PureComponent<Props, State> {
  state: State = {
    bookmark: null,
    lastReportQueryContext: null,
    loading: true,
    isSavingBookmark: false,
    longUrlCount: 0,
    originDashboard: null,
    report: this.getDefaultModel(),
  };

  private disconnectQueue: Array<() => void> = [];

  private declare tracker: ReportTracker;

  get bookmarkStore(): BookmarkStore {
    return this.props.bookmarkStore;
  }

  getCurrentProject(): CurrentProject {
    return this.props.currentProject.get().value;
  }

  resourceCustomSubscriptions = new Map([
    [
      CurrentProjectResource,
      () => {
        if (this.state.report) {
          this.state.report.update({...this.getDefaultModel(), ...this.state.report});
        } else {
          this.setState({
            report: this.getDefaultModel(),
          });
        }

        this.props.reportLayoutManager.updateWhiteBackBackground(true);
      },
    ],
  ]);

  handleJsUrlOrBookmarkRoute = (jsurlOrBookmarkURLSlug: string) => {
    if (isValidBookmarkURLSlug(jsurlOrBookmarkURLSlug)) {
      this.captureBookmarkToQueryWorkflow({bookmarkState: BookmarkState.BookmarkUrlSlug});
      this.setState({bookmarkURLSlug: jsurlOrBookmarkURLSlug, loading: false});
      return;
    }

    if (this.state.bookmarkURLSlug) {
      return;
    }

    // starting the workflow timing now based on the ad-hoc query in the URL, there's a potential that
    // the query is invalid
    this.captureBookmarkToQueryWorkflow({bookmarkState: BookmarkState.AdHoc});
    if (jsurlOrBookmarkURLSlug) {
      this.setState({longUrlCount: this.state.longUrlCount + 1, loading: true});
    }
    this.handleJSURLRoute(jsurlOrBookmarkURLSlug);
    this.setState({loading: false});
    cancelWorkflow(this, Workflow.BookmarkToQuery, BookmarkState.Invalid);
  };

  handleJSURLRoute(rawJsURL) {
    const jsUrlInfo = parseJsUrl(rawJsURL);
    if (jsUrlInfo.valid) {
      if (jsUrlInfo.correctedUrl !== rawJsURL) {
        this.props.navigate(jsUrlInfo.correctedUrl, {replace: true});
      }
      this.handleURLParamsUpdate(jsUrlInfo.data);
    } else {
      this.props.navigate(``, {replace: true});
      this.handleInvalidURLParams();
    }
  }

  captureBookmarkToQueryWorkflow({bookmarkState}: {bookmarkState: BookmarkState}) {
    captureWorkflowTiming(this, {
      key: Workflow.BookmarkToQuery,
      isComplete: flowsQueryStartedWorkflowComplete,
      additionalProps: {
        bookmark: bookmarkState,
        spaNavCount: this.props.navManager.getSpaNavCount(),
        isFastedit: true,
      },
    });
  }

  loadReportOnNavigation = async (reportId: string, possibleJsUrl: string = null) => {
    const parsedReportId = parseInt(reportId);

    this.captureBookmarkToQueryWorkflow({
      bookmarkState: possibleJsUrl ? BookmarkState.SavedWithOverrides : BookmarkState.Saved,
    });
    const parsedOverrides = parseJsUrl(possibleJsUrl);
    const overrides = parsedOverrides.valid ? parsedOverrides.data : {};
    if (!isEmpty(overrides)) {
      this.setState({longUrlCount: this.state.longUrlCount + 1});
    }
    if (this.state.bookmark && this.state.bookmark.id === parsedReportId) {
      this.loadReport(this.state.bookmark, overrides);
    } else {
      this.setState({loading: true});
      const bookmark = await this.bookmarkStore.getOrFetch<FlowsBookmark>(parsedReportId);
      this.props.contentSidenavContext?.updateSidenavState({
        selectedNavItem: bookmark.dashboard.id.toString(),
      });
      this.loadReport(bookmark, overrides);
    }
    this.setState({loading: false});
  };

  async refreshBookmarkById(reportId): Promise<void> {
    try {
      const bookmark = await this.props.bookmarkStore.fetchFresh<FlowsBookmark>(reportId);
      this.setState({bookmark});
    } catch (err) {
      console.error(err);
      const toast = {
        report: getReportName(),
        type: ToastType.ERROR,
        message: `Bookmark not found`,
      };
      addToast(toast);
    }
  }

  getDefaultModel() {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag) => this.hasProjectFeatureFlag(flag));
    const mathTypeSessions = this.hasProjectFeatureFlag(ProjectFeatureFlag.FlowsMathSessions)
      ? ProjectFeatureFlag.FlowsMathSessions
      : undefined;

    return FlowsReport.createDefaultReport({
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
      disableCustomEvents: this.isCustomEventsDisabled(),
      mathTypeSessions,
    });
  }

  handleDefaultRoute = () => {
    const report = this.state.report || this.getDefaultModel();
    this.setState({report, loading: false});
  };

  resetQuery() {
    this.props.navigate(``, {replace: true});
    this.setState({
      report: this.getDefaultModel(),
    });
  }

  getReportParamsFromUrlParams(params): Partial<FlowsReport> {
    let {useQueryTimeSampling, originDashboard, ...reportParams} = params;
    // migrate anchor URLS
    if (!Object.prototype.hasOwnProperty.call(reportParams, `version`)) {
      reporter.info(`Flows anchor params decoded.`, {reportParams});

      // Handle filter by cohort migration
      if (
        Object.prototype.hasOwnProperty.call(reportParams, `filterByCohort`) &&
        Object.prototype.hasOwnProperty.call(reportParams[`filterByCohort`], `value`)
      ) {
        reporter.info(`Flows filterByCohort param decoded.`, {filterByCohort: reportParams.filterByCohort});
        if (reportParams.filterByCohort.value === `all`) {
          reportParams.filterByCohort = null;
        } else {
          reportParams.filterByCohort = {
            id: reportParams.filterByCohort.value,
            negated: reportParams.filterByCohort.negated,
          };
        }
      }

      // transform query time sampling param
      reportParams.querySampling = useQueryTimeSampling === true;

      reportParams = convertAnchorFlowsToFlowsReport(reportParams);
    }

    // default to qts for projects that have access to it in drop off analysis mode.
    // TODO: post closed beta, renable query header and remove this.
    if (
      reportParams.funnelParams &&
      reportParams.funnelParams.funnelBookmark &&
      this.hasProjectFeatureFlag(ProjectFeatureFlag.QTS)
    ) {
      reportParams.querySampling = true;
    }

    if (reportParams.maxSteps !== -1) {
      reportParams.maxSteps = -1;
      reportParams.anchorPosition = FlowAnchorPosition.Step;
    }

    return reportParams;
  }

  handleURLParamsUpdate(params) {
    const reportParams = this.getReportParamsFromUrlParams(params);

    if (params.originDashboard) {
      this.setState({originDashboard: params.originDashboard});
    }

    const report = this.state.report.clone();
    report.update(reportParams);
    this.updateReport({report, bookmarkURLSlug: null});
  }

  handleInvalidURLParams() {
    return this.resetQuery();
  }

  isCustomEventsDisabled(): boolean {
    return this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.FlowsHideCustomEvents);
  }

  componentDidMount() {
    this.props.currentProject.subscribe(this.resourceCustomSubscriptions.get(CurrentProjectResource));
    const {queryKey} = parseUri(getLocation().href);

    if (queryKey[`redirect`]) {
      replaceQueryParamToFragment({queryKey: `redirect`});
    }

    // Set up report tracker
    this.tracker = getReportTracker({
      appName: ReportName.Flows,
      productionEvents: new Set([...ProductionTrackingEvents, ...Object.values(ConversionTrackedEvents)]),
      productionPrefix: `Flows`,
      appPersistentProperties: () => ({
        ...this.state.report.getTrackingProps(),
        'Bookmark id': this.state.bookmark?.id,
        'using new query builder': true,
        'using query builder add steps': true,
      }),
    });

    // we heavily rely on this setState to set queryContext correctly
    // we can't use props directly in `render` because it will trigger an instance `handleUpdate` call
    // but the core-app component is not mounted, so state & navigation is not ready yet
    // so we have one level of redirection in `componentDidMount` for this `setState`
    // to make sure reportContext is only passed to report-editor after the core app is mounted
    // TODO: remove this and support for `lastReportQueryContext` in report-editor once report-editor is converted to react
    // query-context should be read from context in `report-editor#componentDidMount`
    if (this.props.queryContextManager.isDirty) {
      const queryContext = this.props.queryContextManager.query;
      this.setState({
        ...(queryContext.originDashboard && {originDashboard: queryContext.originDashboard}),
        lastReportQueryContext: queryContext,
      });
    }

    const downloadMode = getDownloadModeFromLocation();
    if (downloadMode === DownloadMode.None) {
      this.setState((state) => {
        const dashboardId = state.originDashboard?.id;
        this.props.contentSidenavContext?.setSidenavState({
          ...(dashboardId && {selectedNavItem: dashboardId.toString()}),
        });
      });
    }
  }

  componentWillUnmount() {
    this.disconnectQueue.forEach((disconnectAction) => disconnectAction());
    this.props.currentProject.unsubscribe(this.resourceCustomSubscriptions.get(CurrentProjectResource));
  }

  // workflow requires this method to be implemented
  onDisconnected = (disconnectAction: () => void) => {
    this.disconnectQueue.push(disconnectAction);
  };

  hasProjectFeatureFlag(flag: ProjectFeatureFlag) {
    return this.getCurrentProject()?.hasFeatureFlag(flag);
  }

  updateReport(
    {report, bookmarkURLSlug}: {report: FlowsReport; bookmarkURLSlug: string},
    {bookmark = this.state.bookmark, originDashboard = this.state.originDashboard} = {},
  ) {
    this.setState({report, bookmarkURLSlug});

    let fragment = ``;
    if (bookmarkURLSlug) {
      fragment = bookmarkURLSlug;
    } else {
      const reportUrlData: Obj = report.toUrlData();
      let urlData: Obj = {};

      if (originDashboard) {
        urlData.originDashboard = originDashboard;
      }

      if (bookmark) {
        const savedReportUrlData = FlowsReport.fromBookmark(bookmark).toUrlData();

        if (!isEqual(reportUrlData, savedReportUrlData)) {
          urlData = {...urlData, ...reportUrlData};
        }

        const maybeName = bookmark?.name ? `/${kebabCase(bookmark.name)}` : ``;

        fragment = `report/${bookmark.id}${maybeName}`;
      } else {
        urlData = {...urlData, ...reportUrlData};
      }

      if (!isEmpty(urlData)) {
        const serializedUrlData = serializeJsUrl(urlData);
        fragment = fragment ? `${fragment}/${serializedUrlData}` : serializedUrlData;
      }
    }
    this.props.navigate(fragment, {replace: true});
  }

  private get projectId() {
    return parseInt(getProjectId());
  }

  private get workspaceId() {
    return parseInt(getWorkspaceId());
  }

  private get userId() {
    return userCookieUtils.parseUserIdentityFromCookie().id;
  }

  async saveReport({
    editedBookmark,
    originalBookmark,
    dashboard,
    originDashboard,
    saveAsNew,
  }: SaveEvent<FlowsBookmark>[`detail`]) {
    const modifiedReportData = pick(editedBookmark, [`name`, `description`]);
    const {chartType} = editedBookmark.params;
    this.setState({isSavingBookmark: true});

    try {
      let bookmark: Bookmark<FlowsBookmark>;
      if (saveAsNew) {
        bookmark = await dashboardsClient.addNewDashboardBookmark({
          ...(originDashboard ? {dashboardOrigin: originDashboard} : {}),
          dashboardId: dashboard.id,
          bookmarkData: {
            type: BookmarkType.Flows,
            name: editedBookmark.name,
            params: JSON.stringify(editedBookmark.params),
            icon: ChartTypeIcon[chartType],
            ...modifiedReportData,
            ...(dashboard ? {dashboard_id: dashboard.id} : {}),
          },
          projectId: this.projectId,
          workspaceId: this.workspaceId,
        });
        if (originalBookmark) {
          // saving a new version of an existing report
          this.props.reportEditorActions.openTitleEditor();
        }
      } else {
        bookmark = await this.bookmarkStore.createOrUpdate<FlowsBookmark>(
          {
            type: BookmarkType.Flows,
            name: editedBookmark.name,
            params: editedBookmark.params,
            icon: ChartTypeIcon[chartType],
            ...modifiedReportData,
            ...(dashboard ? {dashboard_id: dashboard.id} : {}),
            id: originalBookmark.id,
          },
          {saveAsNew},
        );
      }

      bookmark.dashboard = dashboard;
      const newReport = FlowsReport.fromBookmark(bookmark);

      this.addReportToRecentsSearchList(bookmark);
      this.setState({
        bookmark,
        isSavingBookmark: false,
      });
      this.updateReport({report: newReport, bookmarkURLSlug: null}, {bookmark, originDashboard});
      this.props.contentSidenavContext?.updateSidenavState({
        selectedNavItem: dashboard.id.toString(),
      });

      return bookmark;
    } catch (error) {
      const err = error as Error;
      addToast({
        message: `Failed to save report: ${err.message}`,
        type: ToastType.ERROR,
      });
      this.setState({isSavingBookmark: false});
      return null;
    }
  }

  async deleteReport(bookmark: Bookmark<FlowsBookmark>) {
    await dashboardsClient.removeDashboardBookmark({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
      bookmark,
    });
    removeRecentItemById(bookmark.id);
    const dashboardLink = getReportUrl(`boards`, {
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });
    navigate(dashboardLink);
    this.setState({bookmark: null});
  }

  loadReport(bookmark: Bookmark<FlowsBookmark>, overrides: BookmarkOverrides = {}) {
    // Reports with multiple anchors v2 enabled should have maxSteps set to -1 to disable
    // the legacy way of controlling steps.
    // Reports created without this flag might not have this be the case so
    // we need to make sure to set maxSteps to -1 when loading the report.
    if (bookmark.params.maxSteps !== -1) {
      bookmark.params.maxSteps = -1;
      bookmark.params.anchorPosition = FlowAnchorPosition.Step;
    }

    if (!bookmark.params.count_type) {
      bookmark.params.count_type = CountType.Unique;
      bookmark.params.conversion_window = {
        unit: ConversionWindowUnit.Day,
        value: 30,
      };
    }

    let originDashboard = null;
    if (`originDashboard` in overrides) {
      originDashboard = overrides[`originDashboard`];
      overrides = omit(overrides, [`originDashboard`]);
    }

    const dateRangeOverride = overrides?.dateRangeOverride ? new DateRange(overrides.dateRangeOverride) : null;
    const additionalFilters = overrides?.additionalFilters
      ? overrides.additionalFilters.map((filter) => new PropertyFilter(filter))
      : null;
    const overridenParams = getBookmarkParamsWithOverrides(
      bookmark.params,
      overrides?.chartTypeOverride,
      dateRangeOverride,
      additionalFilters,
    );

    const report = FlowsReport.fromBookmark({...bookmark, params: overridenParams});
    report.update(this.getReportParamsFromUrlParams({...report.toUrlData(), ...overrides}));

    this.setState({
      originDashboard,
      bookmark,
      report,
    });
    this.addReportToRecentsSearchList(bookmark);
  }

  addReportToRecentsSearchList(bookmark: Bookmark<FlowsBookmark>) {
    searchUtils.addRecentItem(
      transformBookmarkIntoRecentItem(bookmark, `flows`, {
        projectId: this.projectId,
        workspaceId: this.workspaceId,
        userId: this.userId,
      }),
    );
  }

  trackEvent(event: string, properties = {}, includeReportProperties = true, includeReportPrefix = true) {
    this.tracker.track(event, properties, {includeReportProperties, includeReportPrefix});
  }

  getDisplayBookmark = (): DisplayBookmark<FlowsBookmark> => {
    if (this.state.loading) {
      return null;
    }

    const {report} = this.state;
    return {
      name: report.name,
      description: report.description,
      type: BookmarkType.Flows,
      params: report.generateBookmark(),
    };
  };

  handleReportEditorUpdate = (ev: UpdateEvent) => {
    const {bookmark: updatedBookmark, bookmarkURLSlug} = ev.detail;
    const {report: previousReport, bookmark} = this.state;

    // need to manually add the dashboard since we expect it to be on the Report model
    const report = FlowsReport.fromBookmark({
      ...bookmark,
      ...updatedBookmark,
    });

    report.querySampling = previousReport.querySampling;
    this.updateReport({report, bookmarkURLSlug});
  };

  handleReportEditorSave = (ev: SaveEvent) => {
    this.saveReport(ev.detail);
  };

  handleReportEditorDelete = (ev: DeleteEvent) => {
    this.deleteReport(ev.detail.originalBookmark);
  };

  handleReportEditorQuerySampling = (ev) => {
    const report = this.state.report;
    report.querySampling = ev.detail;
    this.updateReport({report, bookmarkURLSlug: null});
  };

  handleReportEditorMove = async (ev: CustomEvent) => {
    try {
      const dashboard = await moveReport(this.state.bookmark?.dashboard.id, getProjectId(), ev, dashboardsClient);

      this.setState({originDashboard: dashboard});

      this.setState({loading: true});
      await this.refreshBookmarkById(this.state.bookmark.id);
      this.setState({loading: false});
      this.props.contentSidenavContext?.updateSidenavState({
        selectedNavItem: dashboard.id.toString(),
      });

      const visitBoard = getCardInDashboardHref({
        dashboardId: dashboard?.id,
        projectId: Number(getProjectId()),
        workspaceId: Number(getWorkspaceId()),
      });
      const toast = {
        cta: `Visit Board`,
        message: `Board has been moved.`,
        type: ToastType.SUCCESS,
        undoable: false,
        ctaHref: visitBoard,
      };
      addToast(toast);
    } catch (err) {
      let message = `Error moving board`;

      if (err instanceof ApiResponseError) {
        message = err.message;
      }

      reporter.error(err as Error, message);
      const toast = {
        message,
        type: ToastType.ERROR,
        timeoutMS: 20000,
      };
      addToast(toast);
    }
  };

  render() {
    return (
      <>
        <BackboneStyleRoutes
          routes={{
            'report/:reportId/:reportNameOrJsURL': this.loadReportOnNavigation,
            'report/:reportId': this.loadReportOnNavigation,
            'report/:reportId/:reportName/:possibleJsURL': this.loadReportOnNavigation,
            ':jsurlOrBookmarkURLSlug': this.handleJsUrlOrBookmarkRoute,
            '': this.handleDefaultRoute,
          }}
        />
        <ReportEditorReact
          originDashboard={this.state.originDashboard}
          loading={this.state.loading}
          isSavingBookmark={this.state.isSavingBookmark}
          elref={`reportEditor`}
          appMode
          enableQuerySampling={this.state.report?.querySampling}
          editorParams={{
            defaultBookmarkType: BookmarkType.Flows,
            originalBookmark: this.state.bookmark,
            editedBookmark: this.getDisplayBookmark(),
            resultsPromise: null,
            dashboardOverrides: null,
            bookmarkURLSlug: this.state.bookmarkURLSlug,
            lastReportQueryContext: this.state.lastReportQueryContext,
            longUrlCount: this.state.longUrlCount,
          }}
          dashboardsRoute={getRouteToCurrentDashboard({
            dashboard: this.state.bookmark?.dashboard,
            includeCurrentDashboard: true,
          })}
          onSave={this.handleReportEditorSave}
          onMove={this.handleReportEditorMove}
          onUpdate={this.handleReportEditorUpdate}
          onDelete={this.handleReportEditorDelete}
          onToggleQuerySampling={this.handleReportEditorQuerySampling}
        />
      </>
    );
  }
}
